// Plan performance, phase 1.
//
// Spec path: GET /marketing-plans/:id/performance
// This app calls the backend under /api:
//   GET /api/marketing-plans/:planId/performance?brandId=&revision=
//
// One payload covers the header, pacing (spendClass new vs existing),
// per-test readouts, the daily forecast vs actual series, creatives,
// and colorways. `unavailableReason` is shown as written when the plan
// cannot be reported yet (for example it has not been pushed). A reason
// does not drop a nested `performance` snapshot, a forecast total, or
// the pacing tiles. Actuals stay empty until live results exist.
//
// This module only reads. It does not refresh Meta or accept suggestions.
// The page and its links render only when VITE_PLAN_PERFORMANCE=true.
// Suggestions render from the page only when VITE_PLAN_SUGGESTIONS=true.
//
// An optional top-level `live` block is server-owned status
// (PERFORMANCE_LIVE_INSIGHTS on an allowlisted service). The client
// does not send `live=true`. When live rows exist, the server replaces
// snapshot rows before this same body is built, so `series` stays the
// phase-1 array.

import { apiJson, isApiError } from '../auth/apiFetch';
import { isMissingEndpoint, isSecretText, safeText } from './client';

export type SpendSplit = {
  newSpend: number | null;
  existingSpend: number | null;
  /** A numeric budget whose spend class is neither new nor existing. */
  unclassified: number | null;
};

export type PacingLine = {
  name: string;
  spendClass: 'new' | 'existing' | null;
  weekly: SpendSplit;
  flight: SpendSplit;
  spent: SpendSplit;
};

export type MetricPair = {
  actual: number | null;
  forecast: number | null;
};

export type TestMetrics = {
  spend: MetricPair;
  roas: MetricPair;
  cpa: MetricPair;
  revenue: MetricPair;
  purchases: MetricPair;
};

export type PerformanceCall = 'ahead' | 'too_early' | 'control_ahead';

export type TestReadout = {
  name: string;
  call: PerformanceCall | null;
  callLabel: string | null;
  confidenceLabel: string | null;
  daysLabel: string | null;
  ours: TestMetrics;
  control: TestMetrics;
};

export type RevenuePoint = {
  dateLabel: string;
  forecast: number | null;
  actual: number | null;
};

export type CreativeResult = {
  name: string;
  typeKey: string;
  typeLabel: string;
  thumbnailUrl: string | null;
  colorway: string | null;
  spend: number | null;
  ctr: number | null;
  cpa: number | null;
  roas: number | null;
  fatigue: string | null;
};

export type CreativeGroup = {
  key: string;
  label: string;
  rows: CreativeResult[];
};

export type ColorwayResult = {
  name: string;
  inStock: number | null;
  sizeRule: number;
  belowRule: boolean;
  spend: number | null;
  revenue: number | null;
  roas: number | null;
  purchases: number | null;
};

export type ForecastTotals = {
  spend: number | null;
  revenue: number | null;
  incremental: number | null;
  purchases: number | null;
  roas: number | null;
  cpa: number | null;
};

export type PlanPerformance = {
  title: string;
  revision: string | null;
  status: string | null;
  flightLabel: string | null;
  refreshedLabel: string | null;
  currency: string | null;
  unavailableReason: string | null;
  forecast: ForecastTotals | null;
  paceLabel: string | null;
  spendToDate: SpendSplit;
  weekly: SpendSplit;
  flight: SpendSplit;
  lines: PacingLine[];
  tests: TestReadout[];
  series: RevenuePoint[];
  creativeGroups: CreativeGroup[];
  colorways: ColorwayResult[];
  hasResults: boolean;
};

/** Server-owned live-insights status. Account ids are not copied onto this view. */
export type LiveSource = 'snapshot' | 'cache' | 'live';

export type LiveReason =
  | 'no-plan'
  | 'not-pushed'
  | 'fresh'
  | 'meta-error'
  | 'no-credential'
  | 'account-not-approved'
  | 'throttled'
  | 'token-decrypt-failed'
  | 'window-empty'
  | 'no-ids'
  | 'truncated'
  | 'empty';

export type LiveInsightsStatus = {
  source: LiveSource;
  /** Known reason code. Unknown text is dropped so a secret cannot be rendered. */
  reason: LiveReason | null;
  /** `fetchedAt` in the viewer's local calendar, or null when it cannot be shown. */
  fetchedAt: string | null;
  /** Insights window in the viewer's local calendar. */
  windowLabel: string | null;
  truncated: boolean;
};

export type PlanPerformanceResult = {
  performance: PlanPerformance | null;
  /** The performance route is not deployed, or the payload could not be read. */
  missing: boolean;
  /** The route answered 404 with a body: this plan is missing or not this brand. */
  notFound: boolean;
  error: string | null;
  /** Present only when the server attached a recognized `live` block. */
  live: LiveInsightsStatus | null;
};

type Rec = Record<string, unknown>;

const CREATIVE_TYPES: { key: string; label: string; aliases: string[] }[] = [
  { key: 'catalog-image', label: 'Catalog image', aliases: ['catalog-image', 'catalog-images', 'catalogue-image', 'catalogue-images'] },
  { key: 'catalog-video', label: 'Catalog video', aliases: ['catalog-video', 'catalog-videos', 'catalogue-video', 'catalogue-videos'] },
  { key: 'static-ad', label: 'Static ad', aliases: ['static-ad', 'static-ads', 'static', 'static-image', 'image-ad'] },
  { key: 'video-ad', label: 'Video ad', aliases: ['video-ad', 'video-ads', 'video'] }
];

const SERIES_CAP = 366;

export function planSuggestionsEnabled(env: { VITE_PLAN_SUGGESTIONS?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_SUGGESTIONS === 'true';
}

export function planPerformanceEnabled(env: { VITE_PLAN_PERFORMANCE?: string } = import.meta.env): boolean {
  return env.VITE_PLAN_PERFORMANCE === 'true';
}

/** Link to the performance page for an accepted or pushed plan, when the flag is on. */
export function planPerformanceLink(
  status: string | null | undefined,
  brandId: string,
  planId: string,
  revision?: string | null
): string | null {
  if (!planPerformanceEnabled()) return null;
  const key = (status ?? '').trim().toLowerCase();
  if (key !== 'accepted' && key !== 'pushed') return null;
  return planPerformanceHref(brandId, planId, revision);
}

export function planPerformanceHref(brandId: string, planId: string, revision?: string | null): string {
  const params = new URLSearchParams({ brandId });
  if (revision) params.set('revision', revision);
  return `/plans/${encodeURIComponent(planId)}/performance?${params}`;
}

export function performanceUrl(brandId: string, planId: string, revision?: string | null): string {
  const params = new URLSearchParams({ brandId });
  if (revision) params.set('revision', revision);
  return `/api/marketing-plans/${encodeURIComponent(planId)}/performance?${params}`;
}

export async function getPlanPerformance(
  brandId: string,
  planId: string,
  revision?: string | null,
  init?: { signal?: AbortSignal }
): Promise<PlanPerformanceResult> {
  try {
    const body = await apiJson<unknown>(performanceUrl(brandId, planId, revision), {
      cache: 'no-store',
      signal: init?.signal
    });
    const performance = readPlanPerformance(body);
    const live = readLiveInsights(body);
    if (!performance) return { performance: null, missing: true, notFound: false, error: null, live: null };
    return { performance, missing: false, notFound: false, error: null, live };
  } catch (err: unknown) {
    if (isAbortError(err)) throw err;
    // A 404 with no body is a route that is not deployed. A 404 with a
    // body is this plan, and it is missing or not the signed-in brand.
    if (isApiError(err) && err.status === 404 && err.body != null) {
      return { performance: null, missing: false, notFound: true, error: null, live: null };
    }
    if (isMissingEndpoint(err)) return { performance: null, missing: true, notFound: false, error: null, live: null };
    return { performance: null, missing: false, notFound: false, error: performanceError(err), live: null };
  }
}

const LIVE_SOURCES = new Set<LiveSource>(['snapshot', 'cache', 'live']);

const LIVE_REASONS = new Set<LiveReason>([
  'no-plan', 'not-pushed', 'fresh', 'meta-error', 'no-credential', 'account-not-approved',
  'throttled', 'token-decrypt-failed', 'window-empty', 'no-ids', 'truncated', 'empty'
]);

/**
 * Reads the optional server `live` block. Series, title, currency, and
 * dates stay on `readPlanPerformance`. This does not accept `meta_insights`
 * or copy `accountId`.
 */
export function readLiveInsights(body: unknown): LiveInsightsStatus | null {
  if (!isRec(body)) return null;
  const live = nested(body.live);
  if (!live) return null;
  const source = live.source;
  if (typeof source !== 'string' || !LIVE_SOURCES.has(source as LiveSource)) return null;
  const reason = typeof live.reason === 'string' && LIVE_REASONS.has(live.reason as LiveReason)
    ? live.reason as LiveReason
    : null;
  return {
    source: source as LiveSource,
    reason,
    fetchedAt: refreshedLabel(live.fetchedAt),
    windowLabel: windowLabelOf(live.window),
    truncated: live.truncated === true
  };
}

function windowLabelOf(value: unknown): string | null {
  if (typeof value === 'string') return formatLocalStamp(value);
  const window = nested(value);
  if (!window) return null;
  const since = formatLocalStamp(window.since ?? window.start ?? window.from);
  const until = formatLocalStamp(window.until ?? window.end ?? window.to);
  if (since && until) return `${since} – ${until}`;
  return since ?? until;
}

function isAbortError(err: unknown): boolean {
  return (err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError');
}

export function readPlanPerformance(body: unknown): PlanPerformance | null {
  const payload = payloadOf(body);
  if (!payload) return null;
  const header = nested(payload.header) ?? nested(payload.plan) ?? nested(payload.summary) ?? {};
  const pacing = nested(payload.pacing) ?? {};
  const currency = currencyCode(header.currency ?? payload.currency ?? pacing.currency);
  const lines = records(pacing.lines ?? pacing.rows ?? pacing.tests ?? pacing.cells)
    .map(readPacingLine)
    .filter((line): line is PacingLine => line != null);
  const summed = lines.reduce(addLineSplits, emptyTotals());
  const spendToDate = coalesce(
    readAggregate(pacing.spendToDate ?? pacing.spent ?? pacing.actualSpend),
    summed.spent
  );
  const weekly = coalesce(readAggregate(pacing.weekly ?? pacing.weeklyBudget), summed.weekly);
  const flight = coalesce(readAggregate(pacing.flight ?? pacing.flightBudget), summed.flight);
  const tests = records(payload.tests ?? payload.readouts).map(readTest);
  const series = readSeries(payload);
  const creativeGroups = groupCreatives(records(payload.creatives));
  const colorways = records(payload.colorways).map(readColorway);
  const paceLabel = readPace(pacing.status ?? pacing.pace ?? header.pace);
  const forecast = readForecastTotals(payload);
  const hasResults = Boolean(paceLabel)
    || splitPresent(spendToDate) || splitPresent(weekly) || splitPresent(flight)
    || lines.length > 0 || tests.length > 0 || series.length > 0
    || creativeGroups.length > 0 || colorways.length > 0
    || forecast != null;

  return {
    title: label(header.name ?? header.title ?? payload.name ?? payload.title, 160) ?? 'Plan performance',
    revision: revisionText(header.revision ?? payload.revision),
    status: label(header.status ?? payload.status, 40),
    flightLabel: readFlightLabel(header, payload),
    refreshedLabel: refreshedLabel(
      header.lastRefreshedAt ?? payload.lastRefreshedAt ?? header.refreshedAt ?? payload.refreshedAt
      ?? header.fetchedAt ?? payload.fetchedAt
    ),
    currency,
    unavailableReason: reasonText(payload.unavailableReason ?? header.unavailableReason, 400),
    forecast,
    paceLabel,
    spendToDate,
    weekly,
    flight,
    lines,
    tests,
    series,
    creativeGroups,
    colorways,
    hasResults
  };
}

function payloadOf(body: unknown): Rec | null {
  if (!isRec(body)) return null;
  const nestedPayload = nested(body.performance);
  if (!nestedPayload) return body;
  // A top-level unavailableReason used to discard the nested snapshot.
  // Keep the snapshot, and let a present top-level field win.
  const merged: Rec = { ...nestedPayload };
  for (const [key, value] of Object.entries(body)) {
    if (key === 'performance' || value == null) continue;
    merged[key] = value;
  }
  return merged;
}

function readForecastTotals(payload: Rec): ForecastTotals | null {
  const forecast = nested(payload.forecast);
  if (!forecast) return null;
  const total = nested(forecast.total) ?? forecast;
  const totals: ForecastTotals = {
    spend: num(total.spend),
    revenue: num(total.revenue),
    incremental: num(total.incrementalRevenue ?? total.incrementalSales),
    purchases: num(total.purchases ?? total.purchaseCount),
    roas: num(total.roas),
    cpa: num(total.cpa)
  };
  return Object.values(totals).some((value) => value != null) ? totals : null;
}

function readPacingLine(row: Rec): PacingLine | null {
  const name = label(row.name ?? row.label ?? row.line ?? row.test ?? row.cell, 160);
  if (!name) return null;
  const spendClass = spendClassOf(row.spendClass ?? row.class);
  return {
    name,
    spendClass,
    weekly: readSplit(row.weekly ?? row.weeklyBudget, spendClass),
    flight: readSplit(row.flight ?? row.flightBudget, spendClass),
    spent: readSplit(row.spent ?? row.spendToDate ?? row.spend, spendClass)
  };
}

function readTest(row: Rec, index: number): TestReadout {
  const call = readCall(row.call ?? row.decision);
  return {
    name: label(row.name ?? row.title ?? row.label, 160) ?? `Test ${index + 1}`,
    call,
    callLabel: callLabel(call),
    confidenceLabel: confidenceLabel(row.confidence ?? row.confidenceLevel),
    daysLabel: daysLabel(row.days ?? row.elapsedDays, row.minDays ?? row.minimumDays ?? row.gateDays),
    ours: readArm(row.ours ?? row.test),
    control: readArm(row.control ?? row.holdout)
  };
}

function readArm(value: unknown): TestMetrics {
  const metrics = emptyMetrics();
  if (!isRec(value)) return metrics;
  const armActual = isRec(value.actual) ? value.actual : null;
  const armForecast = isRec(value.forecast) ? value.forecast : null;
  // actual: null means this arm has no live results. Do not promote
  // ours.forecast / control.forecast, or sibling numbers, into actuals.
  const actualSource = armActual ?? ('actual' in value ? null : value);
  const keys = ['spend', 'roas', 'cpa', 'revenue', 'purchases'] as const;
  for (const key of keys) {
    const direct = value[key];
    if (isMetricPair(direct)) {
      metrics[key] = {
        actual: pairSide(direct.actual, key, 'actual'),
        forecast: pairSide(direct.forecast, key, 'forecast')
      };
      continue;
    }
    metrics[key] = {
      actual: actualSource ? metricNumber(actualSource, key) : null,
      forecast: armForecast ? forecastNumber(armForecast, key) : null
    };
  }
  return metrics;
}

function isMetricPair(value: unknown): value is Rec {
  return isRec(value) && ('actual' in value || 'forecast' in value);
}

function pairSide(value: unknown, key: MetricKey, side: 'actual' | 'forecast'): number | null {
  if (isRec(value)) return side === 'forecast' ? forecastNumber(value, key) : metricNumber(value, key);
  return num(value);
}

type MetricKey = 'spend' | 'roas' | 'cpa' | 'revenue' | 'purchases';

function forecastNumber(source: Rec, key: MetricKey): number | null {
  if (key === 'revenue' && 'incrementalRevenue' in source) {
    const incremental = num(source.incrementalRevenue);
    if (incremental != null) return incremental;
  }
  return metricNumber(source, key);
}

function metricNumber(source: Rec, key: MetricKey): number | null {
  const aliases: Record<MetricKey, string[]> = {
    spend: ['spend'],
    roas: ['roas'],
    cpa: ['cpa'],
    revenue: ['revenue', 'purchaseValue', 'incrementalRevenue'],
    purchases: ['purchases', 'purchaseCount']
  };
  for (const name of aliases[key]) {
    if (!(name in source)) continue;
    const raw = source[name];
    if (isMetricPair(raw)) return num(raw.actual);
    const amount = num(raw);
    if (amount != null) return amount;
  }
  return null;
}

function readSeries(payload: Rec): RevenuePoint[] {
  const byDate = new Map<string, RevenuePoint & { time: number | null; index: number }>();
  const add = (point: Rec, source: 'forecast' | 'mixed', index: number) => {
    const read = readPoint(point, source);
    if (!read.dateLabel || (read.forecast == null && read.actual == null)) return;
    const time = timeOf(point.date ?? point.day ?? point.at);
    const existing = byDate.get(read.dateLabel) ?? { ...read, time, index };
    if (read.forecast != null) existing.forecast = read.forecast;
    if (read.actual != null) existing.actual = read.actual;
    if (existing.time == null) existing.time = time;
    byDate.set(read.dateLabel, existing);
  };
  const forecastBag = nested(payload.forecast);
  records(payload.forecastSeries).forEach((point, index) => add(point, 'forecast', index));
  if (forecastBag) {
    records(forecastBag.forecastSeries ?? forecastBag.series ?? forecastBag.daily ?? forecastBag.points)
      .forEach((point, index) => add(point, 'forecast', index));
  }
  records(payload.series ?? payload.daily ?? chartPoints(payload.chart))
    .forEach((point, index) => add(point, 'mixed', index));
  const rows = [...byDate.values()].sort((a, b) => {
    if (a.time != null && b.time != null && a.time !== b.time) return a.time - b.time;
    return a.index - b.index;
  });
  const capped = rows.length > SERIES_CAP ? rows.slice(rows.length - SERIES_CAP) : rows;
  return capped.map((row) => ({ dateLabel: row.dateLabel, forecast: row.forecast, actual: row.actual }));
}

function chartPoints(value: unknown): unknown {
  if (Array.isArray(value)) return value;
  if (isRec(value)) return value.points ?? value.series ?? value.daily ?? [];
  return [];
}

function readPoint(point: Rec, source: 'forecast' | 'mixed'): RevenuePoint {
  const explicitForecast = pointForecast(point);
  const actualSpecified = 'actual' in point || 'actualRevenue' in point || 'cumulativeActual' in point;
  const explicitActual = firstNum(point, ['actualRevenue', 'cumulativeActual', 'actual']);
  const revenue = firstNum(point, ['revenue']);
  return {
    dateLabel: dateLabel(point.date ?? point.day ?? point.at),
    forecast: explicitForecast ?? (source === 'forecast' ? revenue : null),
    actual: actualSpecified ? explicitActual : (source === 'mixed' ? revenue : null)
  };
}

function pointForecast(point: Rec): number | null {
  const raw = point.forecast;
  if (isRec(raw)) return num(raw.revenue ?? raw.incrementalRevenue ?? raw.forecastRevenue);
  return firstNum(point, ['forecastRevenue', 'cumulativeForecast', 'forecast']);
}

function timeOf(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim() || isSecretText(value)) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

function groupCreatives(rows: Rec[]): CreativeGroup[] {
  const groups = new Map<string, CreativeGroup>();
  rows.forEach((row, index) => {
    const type = creativeType(row.creativeType ?? row.type ?? row.kind);
    const group = groups.get(type.key) ?? { key: type.key, label: type.label, rows: [] };
    group.rows.push(readCreative(row, index, type));
    groups.set(type.key, group);
  });
  const ordered = CREATIVE_TYPES.map((type) => groups.get(type.key)).filter((group): group is CreativeGroup => group != null);
  const known = new Set(CREATIVE_TYPES.map((type) => type.key));
  for (const [key, group] of groups) {
    if (!known.has(key)) ordered.push(group);
  }
  return ordered.filter((group) => group.rows.length > 0);
}

function readCreative(row: Rec, index: number, type: { key: string; label: string }): CreativeResult {
  return {
    name: label(row.name ?? row.title ?? row.label, 160) ?? `Creative ${index + 1}`,
    typeKey: type.key,
    typeLabel: type.label,
    thumbnailUrl: safeThumbnail(row.thumbnailUrl ?? row.thumbnail ?? row.imageUrl ?? row.image),
    colorway: label(row.colorway ?? row.color, 80),
    spend: num(row.spend),
    ctr: num(row.ctr),
    cpa: num(row.cpa),
    roas: num(row.roas),
    fatigue: fatigueLabel(row.fatigue ?? row.fatigued)
  };
}

function readColorway(row: Rec, index: number): ColorwayResult {
  const stock = nested(row.stock);
  const inStock = sizeCount(
    row.inStockSizes ?? row.inStockSizeCount ?? row.sizesInStock ?? row.sizes
    ?? stock?.inStockSizes ?? stock?.sizes
  );
  const ruleValue = num(row.sizeRule ?? row.minInStockSizes ?? stock?.sizeRule);
  const sizeRule = ruleValue != null && ruleValue >= 1 ? ruleValue : 8;
  const explicit = row.belowStockRule === true || row.belowRule === true || row.lowStock === true || stock?.belowRule === true;
  return {
    name: label(row.name ?? row.colorway ?? row.label, 80) ?? `Colorway ${index + 1}`,
    inStock,
    sizeRule,
    belowRule: explicit || (inStock != null && inStock < sizeRule),
    spend: num(row.spend),
    revenue: num(row.revenue ?? row.purchaseValue),
    roas: num(row.roas),
    purchases: num(row.purchases ?? row.purchaseCount)
  };
}

function creativeType(value: unknown): { key: string; label: string } {
  const raw = label(value, 80);
  const key = raw ? raw.toLowerCase().replace(/[\s_]+/g, '-') : '';
  const known = CREATIVE_TYPES.find((type) => type.key === key || type.aliases.includes(key));
  if (known) return { key: known.key, label: known.label };
  if (raw) return { key: `other:${key}`, label: raw };
  return { key: 'other', label: 'Other' };
}

function fatigueLabel(value: unknown): string | null {
  if (value === true) return 'Fatigue';
  if (value === false || value == null) return null;
  if (typeof value === 'string') {
    const text = label(value, 40);
    if (!text) return null;
    if (/^(true|yes|fatigued?)$/i.test(text)) return 'Fatigue';
    return null;
  }
  if (!isRec(value)) return null;
  const flagged = value.flagged === true || value.fatigue === true || value.fatigued === true;
  if (!flagged) return null;
  const reason = label(value.reason ?? value.note, 160);
  return reason ? `Fatigue: ${reason}` : 'Fatigue';
}

function readCall(value: unknown): PerformanceCall | null {
  const raw = label(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'ahead' || key === 'ours ahead' || key === 'winner') return 'ahead';
  if (key === 'too early' || key === 'early') return 'too_early';
  if (key === 'control ahead') return 'control_ahead';
  return null;
}

function callLabel(call: PerformanceCall | null): string | null {
  if (call === 'ahead') return 'Ours ahead';
  if (call === 'too_early') return 'Too early';
  if (call === 'control_ahead') return 'Control ahead';
  return null;
}

function confidenceLabel(value: unknown): string | null {
  const amount = num(value);
  if (amount == null) return null;
  const ratio = amount > 1 && amount <= 100 ? amount / 100 : amount;
  if (ratio < 0 || ratio > 1) return null;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(ratio * 100)}%`;
}

function daysLabel(days: unknown, minDays: unknown): string | null {
  const elapsed = num(days);
  const gate = num(minDays);
  if (elapsed == null || gate == null) return null;
  if (elapsed < 0 || gate < 1 || elapsed > 3660 || gate > 3660) return null;
  return `Day ${formatCount(elapsed)} of ${formatCount(gate)}`;
}

function readPace(value: unknown): string | null {
  const raw = label(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'ahead' || key === 'ahead of pace') return 'Ahead';
  if (key === 'behind' || key === 'behind pace') return 'Behind';
  if (key === 'on pace' || key === 'on track') return 'On pace';
  return null;
}

function readSplit(value: unknown, spendClass: 'new' | 'existing' | null): SpendSplit {
  const parts = readParts(value);
  if (parts) return parts;
  const amount = num(value);
  if (amount == null) return blankSplit();
  if (spendClass === 'existing') return { newSpend: 0, existingSpend: amount, unclassified: null };
  if (spendClass === 'new') return { newSpend: amount, existingSpend: 0, unclassified: null };
  return { newSpend: null, existingSpend: null, unclassified: amount };
}

function readAggregate(value: unknown): SpendSplit {
  const parts = readParts(value);
  if (parts) return parts;
  const amount = num(value);
  if (amount == null) return blankSplit();
  return { newSpend: null, existingSpend: null, unclassified: amount };
}

function readParts(value: unknown): SpendSplit | null {
  if (!isRec(value)) return null;
  const hasNew = 'newSpend' in value || 'new' in value;
  const hasExisting = 'existingSpend' in value || 'existing' in value;
  if (!hasNew && !hasExisting) return null;
  return {
    newSpend: hasNew ? num(value.newSpend ?? value.new) : null,
    existingSpend: hasExisting ? num(value.existingSpend ?? value.existing) : null,
    unclassified: null
  };
}

function blankSplit(): SpendSplit {
  return { newSpend: null, existingSpend: null, unclassified: null };
}

function emptyTotals(): { weekly: SpendSplit; flight: SpendSplit; spent: SpendSplit } {
  return { weekly: blankSplit(), flight: blankSplit(), spent: blankSplit() };
}

function addLineSplits(
  totals: { weekly: SpendSplit; flight: SpendSplit; spent: SpendSplit },
  line: PacingLine
) {
  return {
    weekly: addSplit(totals.weekly, line.weekly),
    flight: addSplit(totals.flight, line.flight),
    spent: addSplit(totals.spent, line.spent)
  };
}

function addSplit(left: SpendSplit, right: SpendSplit): SpendSplit {
  return {
    newSpend: addNum(left.newSpend, right.newSpend),
    existingSpend: addNum(left.existingSpend, right.existingSpend),
    unclassified: addNum(left.unclassified, right.unclassified)
  };
}

function addNum(left: number | null, right: number | null): number | null {
  if (left == null && right == null) return null;
  return (left ?? 0) + (right ?? 0);
}

function coalesce(explicit: SpendSplit, summed: SpendSplit): SpendSplit {
  return {
    newSpend: explicit.newSpend != null ? explicit.newSpend : summed.newSpend,
    existingSpend: explicit.existingSpend != null ? explicit.existingSpend : summed.existingSpend,
    unclassified: explicit.unclassified != null ? explicit.unclassified : summed.unclassified
  };
}

function splitPresent(split: SpendSplit): boolean {
  return split.newSpend != null || split.existingSpend != null || split.unclassified != null;
}

function emptyMetrics(): TestMetrics {
  const pair = (): MetricPair => ({ actual: null, forecast: null });
  return { spend: pair(), roas: pair(), cpa: pair(), revenue: pair(), purchases: pair() };
}

function readFlightLabel(header: Rec, payload: Rec): string | null {
  const ranged = dateRange(header.flight) ?? dateRange(payload.flight);
  return flightLabel(
    header.flightStart ?? payload.flightStart ?? ranged?.start,
    header.flightEnd ?? payload.flightEnd ?? ranged?.end
  );
}

function dateRange(value: unknown): { start: unknown; end: unknown } | null {
  if (!isRec(value)) return null;
  if (!('start' in value) && !('end' in value) && !('from' in value) && !('to' in value)) return null;
  return { start: value.start ?? value.from, end: value.end ?? value.to };
}

function flightLabel(start: unknown, end: unknown): string | null {
  const from = formatLocalStamp(start);
  const to = formatLocalStamp(end);
  if (from && to) return `${from} – ${to}`;
  return from ?? to;
}

/** Calendar dates for the performance view, in the viewer's local time zone. */
function formatLocalStamp(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || isSecretText(text)) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(text);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  }).format(date);
}

function refreshedLabel(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text || isSecretText(text)) return null;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return null;
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  }).format(date);
  return formatted;
}

function dateLabel(value: unknown): string {
  return formatLocalStamp(value) ?? label(value, 40) ?? '';
}

function sizeCount(value: unknown): number | null {
  if (Array.isArray(value)) {
    return value.filter((item) => {
      if (item == null || item === false) return false;
      if (!isRec(item)) return true;
      if (item.inStock === false || item.available === false) return false;
      return true;
    }).length;
  }
  return num(value);
}

function spendClassOf(value: unknown): 'new' | 'existing' | null {
  const raw = label(value, 40);
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/[_-]+/g, ' ');
  if (key === 'new') return 'new';
  if (key === 'existing') return 'existing';
  return null;
}

function currencyCode(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function revisionText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  return label(value, 40);
}

function label(value: unknown, limit: number): string | null {
  const text = safeText(value, limit);
  if (!text || looksLikePii(text)) return null;
  return text;
}

function reasonText(value: unknown, limit: number): string | null {
  if (typeof value !== 'string') return null;
  let cleaned = value
    .replace(/\b[a-f0-9]{24}\b/gi, ' ')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, ' ')
    .replace(/EAA[A-Za-z0-9]{10,}/g, ' ')
    .replace(/\bact_\d{4,}/g, ' ')
    .replace(/bearer\s+\S{8,}/gi, ' ')
    .replace(/\b(secret|password|api[_-]?key|feed[_-]?key)\b\s*[:=]\s*\S+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned || looksLikePii(cleaned) || isSecretText(cleaned)) return null;
  if (cleaned.length > limit) cleaned = `${cleaned.slice(0, Math.max(0, limit - 1)).trimEnd()}…`;
  return cleaned;
}

function looksLikePii(text: string): boolean {
  if (/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/.test(text)) return true;
  if (/\b\d{3}[-.\s]\d{3}[-.\s]\d{4}\b/.test(text)) return true;
  return false;
}

function safeThumbnail(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || text.length > 500 || /\s/.test(text) || isSecretText(text)) return null;
  if (/[?&#](?:key|token|access_token|feed_key|api_key)=/i.test(text)) return null;
  if (/^https:\/\/[^\s]+$/.test(text)) return text;
  if (/^\/(?!\/)[^\s]*$/.test(text)) return text;
  return null;
}

function firstNum(source: Rec, keys: string[]): number | null {
  for (const key of keys) {
    if (key in source) return num(source[key]);
  }
  return null;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function performanceError(err: unknown): string {
  if (err instanceof Error && err.message && !isSecretText(err.message)) return err.message;
  return 'Could not load plan performance.';
}

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function nested(value: unknown): Rec | null {
  return isRec(value) ? value : null;
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
